import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { fmtDate } from "../../lib/format";
import { Plus } from "lucide-react";

export default function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "" });
  const [otpDialog, setOtpDialog] = useState(null); // { email, otp_code, user_id }
  const [otpInput, setOtpInput] = useState("");

  const isAdmin = user?.role === "admin";

  const load = () => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/clients", form);
      setOtpDialog(r.data);
      setOpen(false);
      setForm({ name: "", email: "", mobile: "", password: "" });
      toast.success(`Client created. Mock OTP: ${r.data.otp_code}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onVerify = async () => {
    try {
      await api.post("/admin/clients/verify-otp", { email: otpDialog.email, code: otpInput });
      toast.success("Email verified");
      setOtpDialog(null);
      setOtpInput("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
            Clients
          </div>
          <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">
            All Clients
          </h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              data-testid="purge-test-clients-btn"
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              onClick={async () => {
                try {
                  const p = await api.post("/admin/cleanup/test-clients/preview");
                  const n = p.data.users_matched_count;
                  if (!n) { toast.info("No test clients found — you're clean!"); return; }
                  const cs = p.data.cascade_counts || {};
                  const detail = `${n} test client(s)\n\nCascading deletes:\n• ${cs.portfolios || 0} portfolios\n• ${cs.holdings || 0} holdings\n• ${cs.snapshots || 0} snapshots\n• ${cs.notifications || 0} notifications\n• ${cs.notification_history || 0} notification history rows\n\nProtected preserved: ${(p.data.protected_users_preserved || []).length} admin(s).\n\nProceed?`;
                  if (!window.confirm(detail)) return;
                  const r = await api.post("/admin/cleanup/test-clients/apply");
                  toast.success(`Purged ${r.data.deleted.users || 0} test clients + cascade`);
                  api.get("/admin/clients").then((rr) => setClients(rr.data)).catch(() => {});
                } catch (e) {
                  toast.error(`Cleanup failed: ${e.response?.data?.detail || e.message}`);
                }
              }}
            >
              Purge Test Clients
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn" className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  <Plus className="h-4 w-4 mr-2" /> Add Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create new client</DialogTitle>
                </DialogHeader>
                <form onSubmit={onCreate} className="space-y-4 pt-2">
                  <div>
                    <Label>Full name</Label>
                    <Input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="client-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <Input data-testid="client-mobile-input" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Initial password (optional)</Label>
                    <Input data-testid="client-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" placeholder="leave blank for Google-only" />
                  </div>
                  <DialogFooter>
                    <Button data-testid="submit-create-client" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white">Create & send OTP</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Active Portfolio</TableHead>
                <TableHead>End Date</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-zinc-500">No clients yet.</TableCell></TableRow>
              )}
              {clients.map((c) => (
                <TableRow key={c.user_id} data-testid={`client-row-${c.email}`} className="hover:bg-zinc-50">
                  <TableCell>
                    <Link className="text-zinc-950 hover:underline font-medium" to={`/${user?.role === "crm" ? "crm" : "admin"}/clients/${c.user_id}`}>
                      {c.name || "(unnamed)"}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-zinc-700">{c.email}</TableCell>
                  <TableCell className="font-mono text-sm">{c.mobile || "—"}</TableCell>
                  <TableCell>
                    {c.email_verified ? (
                      <Badge className="bg-green-50 text-green-700 border-green-200">verified</Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200">pending</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.client_status === "active" ? (
                      <Badge className="bg-green-50 text-green-700 border-green-200">active</Badge>
                    ) : c.client_status === "inactive" ? (
                      <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200">inactive</Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200">pending</Badge>
                    )}
                    {c.portfolio_count > 0 && (
                      <span className="ml-2 text-[11px] text-zinc-500 font-mono">{c.active_portfolio_count}/{c.portfolio_count}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{c.active_portfolio ? fmtDate(c.active_portfolio.end_date) : "—"}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <button
                        data-testid={`delete-client-${c.user_id}`}
                        title="Delete this client + all their portfolios, notifications, and data"
                        className="text-xs text-rose-600 hover:text-rose-800 font-medium hover:underline"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Delete client ${c.name} (${c.email}) and all their data (portfolios, notifications, holdings)? This cannot be undone.`)) return;
                          try {
                            const r = await api.delete(`/admin/clients/${c.user_id}`);
                            toast.success(`Deleted ${c.email} — ${r.data.deleted.notifications || 0} notifications cleaned`);
                            api.get("/admin/clients").then((rr) => setClients(rr.data)).catch(() => {});
                          } catch (err) {
                            toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* OTP verification dialog */}
      <Dialog open={!!otpDialog} onOpenChange={(o) => !o && setOtpDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify client email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-zinc-600">
              Mock OTP for <span className="font-mono">{otpDialog?.email}</span>: <span className="font-mono font-bold text-zinc-950">{otpDialog?.otp_code}</span>
            </p>
            <Input data-testid="otp-input" placeholder="Enter 6-digit code" value={otpInput} onChange={(e) => setOtpInput(e.target.value)} />
            <Button data-testid="otp-verify-btn" onClick={onVerify} className="bg-zinc-950 hover:bg-zinc-800 text-white w-full">Verify</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
